import { ArrowRightIcon, BanknotesIcon } from '@heroicons/react/20/solid';

import { Button, EButtonType } from '@/components/Button';
import { ContactBox } from '@/components/ContactBox';
import { DisplayComponent } from '@/components/DisplayComponent';
import { Header } from '@/components/Header';
import { OutcomeCard } from '@/components/OutcomeCard';
import { ProgressBar } from '@/components/ProgressBar';
import { RemoteImage } from '@/components/RemoteImage';
import { EDisplays, setDisplay } from '@/context/display';
import { useAppDispatch } from '@/context/storeHooks';
import { useCopy } from '@/copy';

const baseId = 'screen-NoIncomeIneligible';

/**
 * Shown when someone says they have no regular income.
 *
 * Serviceability would decline them eventually, but it declines them on the
 * loan amount — "consider reducing the amount you'd like to borrow" — which
 * is not something this applicant can act on, and only after a full expenses
 * screen. This answers at the point they tell us, and says what would
 * actually have to change.
 *
 * Framed as what the panel can fund rather than as a judgement about them:
 * lender appetite moves, and this is a door someone may come back through.
 *
 * Two layouts, chosen by the same `failureCard` flag the affordability screen
 * uses, so the two outcomes a partner can reach from Income look alike.
 */
export function NoIncomeIneligible(): JSX.Element {
    // ***** Redux State *****
    const dispatch = useAppDispatch();
    const { outcomes, contact, failureCard } = useCopy();
    const outcome = outcomes.noIncome;

    // ***** Event Handlers *****
    function handleEditIncome(): void {
        // Nothing is cleared: the answer they may want to change is the one
        // that brought them here, so it needs to still be on screen.
        dispatch(setDisplay(EDisplays.INCOME));
    }

    // ***** Render *****
    if (failureCard) {
        return (
            <DisplayComponent baseId={baseId} data-testid={baseId}>
                <Header />
                <ProgressBar />
                <OutcomeCard
                    icon={<BanknotesIcon className='h-5 w-5' />}
                    tint='bg-optionTrack text-panel'
                    title={outcome.title}
                    lines={outcome.body}
                >
                    <Button
                        id={`${baseId}-button-editIncome`}
                        onClick={handleEditIncome}
                        type={EButtonType.CARD_PRIMARY}
                    >
                        <span className='_flex items-center gap-2 text-[15px]'>
                            {contact.editIncome}
                            <ArrowRightIcon className='h-4 w-4' />
                        </span>
                    </Button>
                </OutcomeCard>
                <ContactBox />
            </DisplayComponent>
        );
    }

    return (
        <DisplayComponent baseId={baseId} data-testid={baseId}>
            <Header />
            {/* Smaller than the 160px mark the older ineligible screens use:
                the sentence is what the customer needs, and at that size the
                mark pushed it below the fold on a phone. */}
            <div className='flex-center py-6 relative'>
                <div className='bg-primary h-24 w-24 rounded-full' />
                <RemoteImage fileName='coins.svg' className='absolute h-12' />
            </div>
            {/* One alignment and one spacing mechanism. The older screens
                centre the heading over left-aligned copy and set spacing in
                two places at once, which is what made this read as untidy. */}
            <div className='mx-6 _flex flex-col gap-3'>
                <h3 className='text-xl text-primary font-bold'>
                    {outcome.title}
                </h3>
                {outcome.body.map((line, i) => (
                    <p
                        key={line}
                        // The first line carries the outcome; the rest are the
                        // qualifications and sit back, as on the card variant.
                        className={
                            i === 0 ? 'text-secondary' : 'text-sm text-textMuted'
                        }
                    >
                        {line}
                    </p>
                ))}
            </div>
            <div className='_flex space-x-2 mx-6 pt-6'>
                <Button
                    id={`${baseId}-button-editIncome`}
                    onClick={handleEditIncome}
                >
                    {contact.editIncome}
                </Button>
            </div>
            <div className='flex-center w-full'>
                <ContactBox />
            </div>
        </DisplayComponent>
    );
}
